import { DisconnectReason } from 'livekit-client';
import { Sentry } from '@/services/sentry';

/**
 * How the call screens should treat a LiveKit `RoomEvent.Disconnected`.
 *
 * The backend dials the WhatsApp/PSTN party the moment a token is issued, so
 * the guide's LiveKit leg dropping does **not** end the call on its own: unless
 * someone tells the backend, the client keeps ringing (or stays connected to an
 * empty room). Which side is responsible depends on *why* we disconnected:
 *
 * - `hung-up` — we called `room.disconnect()` ourselves. The hang-up path has
 *   already sent `POST /call/terminate`; just wind the screen down.
 * - `ended-remotely` — the backend/LiveKit closed the room or removed us (the
 *   other party hung up, the call finished server-side, or another session
 *   took over our identity). The backend already tore its own leg down.
 * - `failed` — everything else: the initial join failed, the reconnect window
 *   expired, media broke. Nobody has told the backend, so the screen must
 *   terminate the call and show an error instead of a silent "Call ended".
 */
export type DisconnectOutcome = 'hung-up' | 'ended-remotely' | 'failed';

export function classifyDisconnect(reason: DisconnectReason | undefined): DisconnectOutcome {
    switch (reason) {
        case DisconnectReason.CLIENT_INITIATED:
            return 'hung-up';
        case DisconnectReason.ROOM_DELETED:
        case DisconnectReason.ROOM_CLOSED:
        case DisconnectReason.PARTICIPANT_REMOVED:
        case DisconnectReason.DUPLICATE_IDENTITY:
            return 'ended-remotely';
        default:
            return 'failed';
    }
}

/** Shown when the room errors without a usable disconnect reason. */
export const CALL_FAILED_MESSAGE = 'Could not connect to the call. Please try again.';

/** User-facing message for a `failed` disconnect. */
export function describeDisconnect(reason: DisconnectReason | undefined): string {
    switch (reason) {
        case DisconnectReason.SIGNAL_CLOSE:
        case DisconnectReason.SERVER_SHUTDOWN:
        case DisconnectReason.MIGRATION:
        case DisconnectReason.STATE_MISMATCH:
            return 'The call connection was lost.';
        case DisconnectReason.MEDIA_FAILURE:
            return 'Call audio failed.';
        default:
            // JOIN_FAILURE, CONNECTION_TIMEOUT, UNKNOWN_REASON, undefined…
            return CALL_FAILED_MESSAGE;
    }
}

/** Enum name for logs and Sentry tags (`JOIN_FAILURE`), or the raw value. */
export function disconnectReasonName(reason: DisconnectReason | undefined): string {
    if (reason === undefined) return 'undefined';
    return DisconnectReason[reason] ?? String(reason);
}

/**
 * Reports a call-path failure to Sentry, tagged so call issues can be filtered
 * from chat noise. `error` may be a real `Error` (LiveKit `onError`, a rejected
 * terminate) or a string describing a non-exception failure such as a
 * disconnect reason, in which case an `Error` is synthesised for grouping.
 */
export function reportCallFailure(
    error: unknown,
    context: { stage: 'room' | 'disconnect' | 'terminate'; roomName?: string; reason?: string },
): void {
    const exception = error instanceof Error ? error : new Error(String(error));
    Sentry.captureException(exception, {
        tags: {
            call_stage: context.stage,
            call_reason: context.reason ?? 'n/a',
        },
        extra: { roomName: context.roomName },
    });
}
